// Driver offboarding: the shapes the offboarding/* handlers return, the words
// for their codes, and the few pure helpers the screens share. Every figure and
// every rule is Postgres's (db/migrations/0610 to 0616); this file only names
// things. Kept free of React so web/test can import it.
import type { ChipTone } from '../components/interviews/Chip'

// The same three formatters as utils/loans.ts, repeated rather than re-exported
// so that web/test can import this file under node's type stripping, which does
// not resolve an extensionless import.
const ZAR = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export function formatZar(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—'
  const n = Number(v)
  if (!Number.isFinite(n)) return '—'
  return 'R' + ZAR.format(n).replace(/,/g, ' ')
}

/** Times arrive in UTC and are always shown in Johannesburg time. */
export function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('en-ZA', {
    timeZone: 'Africa/Johannesburg', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

export function formatDay(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-ZA', { timeZone: 'Africa/Johannesburg', day: 'numeric', month: 'short', year: 'numeric' })
}

export type Access = {
  can_view: boolean
  can_open: boolean
  can_edit_case: boolean
  can_sign_off: boolean
  can_approve: boolean
  can_see_interview: boolean
  can_see_pay: boolean
  is_finance: boolean
}

export type CaseStatus = 'open' | 'awaiting_signoff' | 'signed_off' | 'closed' | 'cancelled'
export type ItemStatus = 'open' | 'done' | 'not_applicable'

export type CaseSummary = {
  id: string
  case_ref: string
  status: CaseStatus
  driver_id: string
  person_id: string
  employee_name: string | null
  vehicle: string | null
  driver_servant: string | null
  exit_type: string | null
  opened_on: string
  opened_via: 'status_change' | 'by_hand'
  last_working_day: string
  dates_confirmed: boolean
  employment_end_date: string
  pay_due_by: string
  days_to_pay_due: number
  items_total: number
  items_done: number
  items_na: number
  items_open: number
  items_overdue: number
  mine_open: number
  final_pay_late: boolean
  final_pay_paid: boolean
  signed_off_by: string | null
  signed_off_at: string | null
  approved_by: string | null
  approved_at: string | null
  rehire_eligible: 'yes' | 'no' | 'review' | null
  rehire_reason: string | null
  cancelled_reason: string | null
}

export type CaseList = {
  access: Access
  auto_open_on_status_change?: boolean
  cases: CaseSummary[]
  failures: { driver: string | null; to_status: string; error: string; failed_at: string }[]
}

export type Item = {
  item_no: number
  section: string
  section_label: string
  title: string
  guidance: string
  evidence_hint: string
  owner: string | null
  owner_basis: string
  is_mine: boolean
  can_tick: boolean
  start_date: string
  target_date: string
  status: ItemStatus
  evidence: string | null
  not_applicable_reason: string | null
  na_automatic: boolean
  done_by: string | null
  done_at: string | null
  overdue: boolean
  red: boolean
  task_ref: number | null
}

export type PayLine = {
  key: string
  kind: 'earning' | 'deduction'
  label: string
  amount: number | null
  source: string
  basis: string
}

export type Outstanding = { label: string; amount: number | null; detail: string }

export type RelayPayload = {
  simplepay_employee_id: string | null
  payslip_date: string
  attributes: Record<string, string>
  enter_by_hand: { label: string; amount: number; where: string }[]
  approved: boolean
  relayed_at: string | null
  relay_response: { http_status: number; body: unknown } | null
}

export type FinalPay =
  | { prepared: false }
  | {
      prepared: true
      id: string
      period_start: string
      period_end: string
      payslip_date: string
      lines: PayLine[]
      outstanding: Outstanding[]
      basis: Record<string, unknown>
      earnings: number
      deductions: number
      net: number
      prepared_by: string | null
      prepared_at: string
      approved_by: string | null
      approved_at: string | null
      relay: RelayPayload | null
    }

export type Schedule = {
  period_start: string
  period_end: string
  payslip_date: string
  pay_due_by: string
  lines: PayLine[]
  outstanding: Outstanding[]
  earnings: number
  deductions: number
  net: number
  leave_read: boolean
  basis: Record<string, unknown>
}

export type Question = {
  q_no: number
  prompt: string
  kind: 'reason' | 'text' | 'choice' | 'score'
  options: string[] | null
  scored: boolean
  optional: boolean
}

export type Answer = { choice?: string; score?: number; text?: string }

export type Interview = {
  status: 'offered' | 'held' | 'declined'
  offered_on: string
  held_on: string | null
  mode?: 'face_to_face' | 'phone' | null
  answers?: Record<string, Answer>
  interviewer?: string | null
}

export type CaseDetail = {
  ok: true
  access: Access
  final_pay_role: { can_prepare: boolean; can_check: boolean; can_see: boolean }
  summary: CaseSummary
  case: {
    exit_type: string | null
    job_title: string
    notice_received_or_given_date: string | null
    last_working_day: string
    employment_end_date: string
    dates_confirmed: boolean
    dates_confirmed_by: string | null
    dates_confirmed_at: string | null
    service_start_date: string | null
    notice_period_required_days: number | null
    notice_position: string | null
    notice_position_by: string | null
    notice_position_note: string | null
    source_document_kind: string | null
    source_document_ref: string | null
    resignation_in_writing: boolean | null
    hearing_date: string | null
    hearing_chair_person_id: string | null
    hearing_chair: string | null
    outcome_document_ref: string | null
    written_reasons_given_on: string | null
    exit_reason_primary: string | null
    exit_reason_detail: string | null
    rehire_eligible: 'yes' | 'no' | 'review' | null
    rehire_reason: string | null
    certificate_include_reason: boolean
    leave_days_simplepay: number | null
    leave_read_for_date: string | null
    leave_read_at: string | null
    letter_sent_at: string | null
    signoff_note: string | null
    approval_note: string | null
    driver_servant_basis: string | null
    driver_status: string | null
    has_email: boolean
    simplepay_linked: boolean
  }
  missing: string[]
  items: Item[]
  notes: { body: string; author: string; at: string }[]
  interview: Interview | null
  questions: Question[] | null
  final_pay: FinalPay | null
}

export type DriverOffboarding = {
  access: Access
  driver_id?: string | null
  live_case?: CaseSummary | null
  past_cases?: CaseSummary[]
  rehire?: {
    case_ref: string
    rehire_eligible: 'yes' | 'no' | 'review' | null
    rehire_reason: string | null
    exit_type: string | null
    employment_end_date: string
    status: CaseStatus
  } | null
}

// The exit types, in the order a person thinks of them. The keys are the check
// constraint on driver_offboarding_case.exit_type.
export const EXIT_TYPES: { value: string; label: string; dismissal: boolean }[] = [
  { value: 'resignation', label: 'Resignation', dismissal: false },
  { value: 'dismissal_misconduct', label: 'Dismissal: misconduct', dismissal: true },
  { value: 'dismissal_incapacity_performance', label: 'Dismissal: incapacity, performance', dismissal: true },
  { value: 'dismissal_incapacity_ill_health', label: 'Dismissal: incapacity, ill health', dismissal: true },
  { value: 'retrenchment_operational', label: 'Retrenchment (operational requirements)', dismissal: true },
  { value: 'probation_not_confirmed', label: 'Probation not confirmed', dismissal: true },
  { value: 'fixed_term_ended', label: 'Fixed term ended', dismissal: false },
  { value: 'mutual_separation', label: 'Mutual separation', dismissal: false },
  { value: 'absconded', label: 'Absconded', dismissal: false },
  { value: 'death', label: 'Death', dismissal: false },
]

export const EXIT_REASONS: { value: string; label: string }[] = [
  { value: 'pay_level', label: 'Pay level' },
  { value: 'pay_timing', label: 'Pay timing' },
  { value: 'shift_pattern', label: 'Shift pattern' },
  { value: 'vehicle_condition', label: 'Vehicle condition' },
  { value: 'manager_relationship', label: 'Relationship with manager' },
  { value: 'better_offer', label: 'Better offer elsewhere' },
  { value: 'relocation', label: 'Relocation' },
  { value: 'family', label: 'Family' },
  { value: 'studies', label: 'Studies' },
  { value: 'health', label: 'Health' },
  { value: 'conduct', label: 'Conduct' },
  { value: 'performance', label: 'Performance' },
  { value: 'other', label: 'Other' },
]

export const NOTICE_POSITIONS: { value: string; label: string }[] = [
  { value: 'worked', label: 'Worked' },
  { value: 'paid_in_lieu', label: 'Paid in lieu' },
  { value: 'waived', label: 'Waived' },
  { value: 'not_applicable', label: 'Not applicable' },
]

export const CHOICE_LABELS: Record<string, string> = {
  always: 'Always', mostly: 'Mostly', sometimes: 'Sometimes', rarely: 'Rarely',
  fair: 'Fair', understood: 'Understood', confusing: 'Confusing',
  yes_and_yes: 'Yes, and would tell a friend',
  yes_but_not_a_friend: 'Yes, but would not tell a friend',
  no_but_a_friend: 'No, but would tell a friend',
  no_and_no: 'No, and would not tell a friend',
}

export function exitTypeLabel(v: string | null | undefined): string {
  if (!v) return 'Exit type not recorded'
  return EXIT_TYPES.find((t) => t.value === v)?.label ?? v.replace(/_/g, ' ')
}

export function isDismissal(v: string | null | undefined): boolean {
  return EXIT_TYPES.some((t) => t.value === v && t.dismissal)
}

export function reasonLabel(v: string | null | undefined): string {
  if (!v) return 'Not recorded'
  return EXIT_REASONS.find((r) => r.value === v)?.label ?? v.replace(/_/g, ' ')
}

export function choiceLabel(v: string): string {
  return CHOICE_LABELS[v] ?? EXIT_REASONS.find((r) => r.value === v)?.label ?? v.replace(/_/g, ' ')
}

export const STATUS_LABEL: Record<CaseStatus, string> = {
  open: 'Open',
  awaiting_signoff: 'Waiting for sign off',
  signed_off: 'Waiting for summary sign off',
  closed: 'Closed',
  cancelled: 'Cancelled',
}

export const STATUS_TONE: Record<CaseStatus, ChipTone> = {
  open: 'amber',
  awaiting_signoff: 'brand',
  signed_off: 'brand',
  closed: 'green',
  cancelled: 'neutral',
}

/** The line under a case in the list: how final pay stands against its legal date. */
export function payDueText(s: Pick<CaseSummary, 'final_pay_paid' | 'days_to_pay_due' | 'status'>): { text: string; tone: ChipTone } {
  if (s.final_pay_paid) return { text: 'Final pay paid', tone: 'green' }
  if (s.status === 'cancelled') return { text: 'Cancelled', tone: 'neutral' }
  const d = s.days_to_pay_due
  if (d < 0) return { text: `Final pay ${-d} day${d === -1 ? '' : 's'} late`, tone: 'red' }
  if (d === 0) return { text: 'Final pay due today', tone: 'red' }
  return { text: `Final pay due in ${d} day${d === 1 ? '' : 's'}`, tone: d <= 2 ? 'amber' : 'neutral' }
}

/** Steps settled out of the total, counting not applicable as settled. */
export function progress(s: Pick<CaseSummary, 'items_total' | 'items_done' | 'items_na'>): { settled: number; total: number; pct: number } {
  const settled = s.items_done + s.items_na
  const total = s.items_total
  return { settled, total, pct: total === 0 ? 0 : Math.round((settled / total) * 100) }
}

/** Groups the 26 items A to G in their order. */
export function groupItems(items: Item[]): { section: string; label: string; items: Item[] }[] {
  const out: { section: string; label: string; items: Item[] }[] = []
  for (const it of [...items].sort((a, b) => a.item_no - b.item_no)) {
    let g = out.find((x) => x.section === it.section)
    if (!g) { g = { section: it.section, label: it.section_label, items: [] }; out.push(g) }
    g.items.push(it)
  }
  return out.sort((a, b) => a.section.localeCompare(b.section))
}
